import { useState, useEffect, useMemo } from 'react';
import { TextInput, StyleSheet } from 'react-native';
import { Icon, type IconType, styles as baseStyles, theme } from '@socrait/react';

import { AccordionBase, useAccordionState, CopyText } from '~/components/Accordion';
import { ActionItemsScorecardType } from '~/services/types';
import { useDraftMessageMutation } from '~/services/review/useDraftMessageMutation';
import { showError } from '~/utils/showError';

import { StatusButton } from './StatusButton';
import { BaseActionItemProps } from './ActionItemTypes';

const getType = (type: ActionItemsScorecardType): IconType => {
  if (type === 'absent_students') {
    return 'square';
  }
  if (type === 'praised_students') {
    return 'star';
  }
  if (type === 'warned_students') {
    return 'hexagon';
  }
  return 'square';
};

export const DraftMessageActionItem = ({
  type,
  studentVoiceName,
  actionId,
  message,
}: Omit<BaseActionItemProps, 'title'>) => {
  const { isCollapsed, accordionState, setAccordionState, handleToggleCollapse, handleReviewButtonClick } =
    useAccordionState();

  const { mutate: draftMessageMutate, status: draftStatus, message: draftedMessage } = useDraftMessageMutation();
  const [text, setText] = useState(message);

  const title = useMemo(() => {
    const typeText = type === 'praised_students' ? 'praises' : 'warnings';
    return `Message about ${typeText} for **${studentVoiceName}**`;
  }, [studentVoiceName, type]);

  useEffect(() => {
    if (draftStatus === 'success' && draftedMessage?.body && text !== draftedMessage.body) {
      setText(draftedMessage.body);
    }
  }, [draftStatus, draftedMessage, text]);

  // Handle accordion state for draft message types
  useEffect(() => {
    if (draftStatus === 'pending') {
      setAccordionState('loading');
    } else if (draftStatus === 'success') {
      setAccordionState('loaded');
    }
  }, [draftStatus, type, setAccordionState]);

  const onDraft = () => {
    draftMessageMutate(
      {
        actionItemId: actionId,
      },
      {
        onError: (error) => {
          showError(error, 'Failed to draft message. Please try again, or contact support if the issue persists.');
        },
      },
    );
  };

  const onReview = () => {
    handleReviewButtonClick();
  };

  const getRightControls = () => {
    return <StatusButton type={type!} status={draftStatus} onDraft={onDraft} onReview={onReview} />;
  };

  const getCopyText = (): CopyText => {
    const safeText = text || '';

    return {
      plain: safeText,
      html: safeText,
    };
  };

  // Content rendering
  const getContent = () => {
    return (
      <TextInput
        editable
        multiline
        numberOfLines={18}
        maxLength={6000}
        onChangeText={(text: string) => setText(text)}
        value={text}
        style={styles.textInput}
      />
    );
  };

  return (
    <AccordionBase
      title={title}
      icon={<Icon type={getType(type!)} isSolid={true} />}
      rightControls={getRightControls()}
      content={getContent()}
      copyText={getCopyText()}
      isCollapsed={isCollapsed}
      onToggleCollapse={handleToggleCollapse}
      state={accordionState}
      autoExpand={false}
      onReviewButtonClick={handleReviewButtonClick}
    />
  );
};

const styles = StyleSheet.create({
  textInput: {
    ...baseStyles.text,
    fontSize: theme.fontSizes.small,
    lineHeight: theme.lineHeights.small,
    padding: theme.spacing.medium,
  },
});
